import { useState, useEffect, useMemo, FormEvent, ReactElement } from 'react';
import { DynamoDB } from 'aws-sdk';
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Button,
  Box,
  Chip,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import {
  PageTitle,
  CodeEditor,
  CodeSnippetViewer,
  DynamoDBItemsTable,
  LoadingFragment,
  ControlledTabPanels,
} from '@localstack/ui';
import { useRoutes, useAwsGetter, useAwsKeysMemo, useLocalStorage } from '@localstack/services';
import { DEFAULT_DYNAMODB_ROUTES, STORAGE_DYNAMODB_PARTIQL_STATEMENT } from '@localstack/constants';

import { NavTabs } from './components';
import { DynamoDBProps } from './types';

const PAGE_SIZE = 100;
const DEFAULT_DDB_STATEMENT = 'SELECT * FROM "table-name"';

export const DynamoDBPartiQL = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_DYNAMODB_ROUTES,
}: DynamoDBProps): ReactElement => {
  const { goto, generateUrl } = useRoutes();

  const { data: savedStatement, setData: saveStatement } = useLocalStorage<string>({
    ...STORAGE_DYNAMODB_PARTIQL_STATEMENT,
  });

  const statement = savedStatement ?? DEFAULT_DDB_STATEMENT;
  const setStatement = (value: string) => saveStatement(value);
  const [statementToExecute, setStatementToExecute] = useState<string | undefined>(undefined);
  const [counter, setCounter] = useState(0);
  const [page, setPage] = useState(0);
  const [paginationKey, setPaginationKey] = useState<any>(null); // eslint-disable-line

  const { data: tableNames } = useAwsGetter('DynamoDB', 'listTables', [], { clientOverrides });

  const extractedTableName = useMemo(() => statementToExecute?.match(/FROM\s+"([^"]+)"/i)?.[1], [statementToExecute]);

  const { data: tableDescription } = useAwsGetter(
    'DynamoDB',
    'describeTable',
    [{ TableName: extractedTableName ?? '' }],
    { clientOverrides, enable: !!extractedTableName },
  );

  const { data: result, isLoading } = useAwsGetter(
    'DynamoDB',
    'executeStatement',
    [{ Statement: statementToExecute, NextToken: paginationKey }],
    { defaultValue: { Items: [], LastEvaluatedKey: undefined }, clientOverrides },
  );

  const queryKeys = useAwsKeysMemo(page, result?.LastEvaluatedKey);

  const handleExecuteStatement = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPage(0);
    setCounter(counter + 1);
    setStatementToExecute(`${statement}${' '.repeat(counter)}`);
  };

  useEffect(() => {
    setPaginationKey(queryKeys[page]);
  }, [page]);

  const filteredTables = useMemo(() => tableNames?.TableNames ?? [], [tableNames?.TableNames]);

  const hasExecuted = statementToExecute !== undefined;
  const unmarshalledItems = useMemo(
    () => (result?.Items ?? []).map((item) => DynamoDB.Converter.unmarshall(item)),
    [result?.Items],
  );

  const TableViewPanel = useMemo(
    () => (
      <LoadingFragment loading={isLoading} variant="list" height={50} size={8}>
        <DynamoDBItemsTable
          page={page}
          pageSize={PAGE_SIZE}
          table={tableDescription?.Table ?? null}
          rows={result?.Items ?? []}
          loading={isLoading}
          hasMore={!!result?.LastEvaluatedKey}
          onPageChange={setPage}
          onViewRow={(_row, hashKey, rangeKey) => {
            const url = generateUrl(routes.RESOURCES_DYNAMODB_TABLE_ITEM_RANGE_UPDATE, {
              tableName: extractedTableName,
              hashKey,
              rangeKey,
            });
            window.open(url, '_blank');
          }}
        />
      </LoadingFragment>
    ),
    [
      isLoading,
      page,
      result?.Items,
      result?.LastEvaluatedKey,
      tableDescription,
      extractedTableName,
      generateUrl,
      routes,
      setPage,
    ],
  );

  const JsonViewPanel = useMemo(
    () => <CodeSnippetViewer data={JSON.stringify(unmarshalledItems, null, 2)} disableParsing />,
    [unmarshalledItems],
  );

  const tabPanelOptions = useMemo(
    () => [
      { label: 'Table view', panel: TableViewPanel },
      { label: 'JSON view', panel: JsonViewPanel },
    ],
    [TableViewPanel, JsonViewPanel],
  );

  return (
    <Layout
      documentTitle="PartiQL"
      tabs={<NavTabs routes={routes} />}
      title={
        <PageTitle
          title="PartiQL Editor"
          breadcrumbs={[
            ['DynamoDB', () => goto(routes.RESOURCES_DYNAMODB)],
            ['PartiQL', null],
          ]}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardHeader
              title="Tables"
              titleTypographyProps={{ variant: 'subtitle1' }}
              action={<Chip label={tableNames?.TableNames?.length ?? 0} size="small" />}
            />
            <Divider />
            <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredTables.map((name) => (
                <ListItemButton key={name} onClick={() => setStatement(`SELECT * FROM "${name}"`)}>
                  <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
              {filteredTables.length === 0 && (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    No tables found
                  </Typography>
                </Box>
              )}
            </List>
          </Card>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card variant="outlined">
            <CardHeader
              title="Statement editor"
              subheader="Run PartiQL statements against your DynamoDB tables"
              titleTypographyProps={{ variant: 'subtitle1' }}
              subheaderTypographyProps={{ variant: 'caption' }}
            />
            <CardContent sx={{ pt: 0 }}>
              <form id="statement" onSubmit={handleExecuteStatement}>
                <CodeEditor language="sql" setValue={setStatement} value={statement} />
              </form>
            </CardContent>
            <CardActions>
              <Button type="submit" form="statement" color="primary" variant="contained">
                Run
              </Button>
              <Button variant="outlined" onClick={() => setStatement('')}>
                Clear
              </Button>
            </CardActions>
          </Card>

          <Box mt={2}>
            <Card>
              <CardHeader
                title="Items returned"
                titleTypographyProps={{ variant: 'subtitle1' }}
                action={
                  hasExecuted && !isLoading ? (
                    <Chip
                      label={`${result?.Items?.length ?? 0}${result?.LastEvaluatedKey ? '+' : ''} items`}
                      size="small"
                    />
                  ) : null
                }
              />
              <Divider />
              {!hasExecuted ? (
                <Box py={6} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Run a statement above to see results
                  </Typography>
                </Box>
              ) : (
                <ControlledTabPanels options={tabPanelOptions} />
              )}
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
};
